import React, { useEffect } from 'react';
import { Box, IconButton, Typography } from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { ChevronLeft } from 'lucide-react';
import CustomPagination from 'components/common/pagination/pagination';
import { commonGridStyles } from 'styles/gridStyles';

interface StatDataGridDetailProps {
  onBack: () => void;
  title: string;
  columns: GridColDef[];
  theme?: string;
  fetchFn?: (page: number, limit: number) => Promise<{ rows: any[]; total: number }>;
}

const StatDataGridDetail: React.FC<StatDataGridDetailProps> = ({
  onBack,
  title,
  columns,
  theme = 'dark',
  fetchFn,
}) => {
  const isDark = theme === 'dark';
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(10);
  const [rows, setRows] = React.useState<any[]>([]);
  const [total, setTotal] = React.useState(0);
  const [loading, setLoading] = React.useState(false);

  useEffect(() => {
    if (fetchFn) {
      let isMounted = true;
      setLoading(true);
      fetchFn(page + 1, rowsPerPage)
        .then((data) => {
          if (isMounted) {
            setRows(data.rows || []);
            setTotal(data.total || 0);
          }
        })
        .catch((err) => {
          console.error('Failed to fetch data', err);
        })
        .finally(() => {
          if (isMounted) setLoading(false);
        });

      return () => {
        isMounted = false;
      };
    }
  }, [fetchFn, page, rowsPerPage]);

  const handlePageChange = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const renderPagination = () => (
    <CustomPagination
      count={total}
      page={page}
      onPageChange={handlePageChange}
      rowsPerPage={rowsPerPage}
      onRowsPerPageChange={handleRowsPerPageChange}
      rowsPerPageOptions={[10, 25, 50, 100]}
      sx={{
        color: isDark ? '#8698a6' : '#5d7182',
        borderBottom: 'none',
      }}
    />
  );

  return (
    <Box
      sx={{
        bgcolor: isDark ? 'var(--bg-panel)' : '#ffffff',
        borderRadius: '12px',
        border: isDark ? '1px solid var(--border-color)' : '1px solid #e0e6ed',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Box
        sx={{
          p: 2,
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          borderBottom: isDark ? '1px solid var(--border-color)' : '1px solid #e0e6ed',
        }}
      >
        <IconButton onClick={onBack} sx={{ color: isDark ? '#8698a6' : '#5d7182' }}>
          <ChevronLeft size={24} />
        </IconButton>
        <Typography variant="h6" sx={{ color: isDark ? '#fff' : '#1a2c38', fontWeight: 600 }}>
          {title}
        </Typography>
      </Box>

      <Box sx={{ flex: 1, p: 3, display: 'flex', flexDirection: 'column' }}>
        {renderPagination()}
        <DataGrid
          rows={rows}
          columns={columns}
          disableRowSelectionOnClick
          hideFooterPagination
          disableColumnMenu
          loading={loading}
          sx={[
            ...(Array.isArray(commonGridStyles) ? commonGridStyles : [commonGridStyles]),
            { flex: 1, minHeight: 400 },
          ]}
        />
        {renderPagination()}
      </Box>
    </Box>
  );
};

export default StatDataGridDetail;
